// R1 — presentation mapping for the admin inquiry timeline
// (GET /api/v1/admin/inquiries/:id/timeline). Pure functions; unit tests in
// src/lib/__tests__/timelineEvents.test.ts pin the label vocabulary.
//
// The backend event stream is the digavouch.inquiry_timeline_events view
// (forensics §10 productized). severity === "error" marks the rows the
// screen renders in red — failed OAuth attempts, failed emails, and IDV
// captures that arrived via the reconciliation backstop (both the client
// and the webhook missed: the John-class marker).
//
// This is an ADMIN forensics surface (allowlisted admins only) — the banned
// operator-vocabulary rules apply to operator/applicant UI, and raw provider
// reasons/statuses render verbatim here on purpose.

export interface TimelineEvent {
  at: string;
  event: string;
  severity: "error" | "warn" | null;
  detail: Record<string, unknown>;
}

export interface EventPresentation {
  /** Short human line, e.g. "google connected". */
  label: string;
  /** Optional secondary line with the load-bearing detail. */
  sub: string | null;
  /** True when the row belongs to a voucher's side of the flow. */
  isVoucher: boolean;
}

const str = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);

export function describeEvent(e: TimelineEvent): EventPresentation {
  const d = e.detail ?? {};
  const isVoucher = d.is_voucher === true;
  switch (e.event) {
    case "inquiry.created":
      return { label: "Inquiry created", sub: str(d.applicant), isVoucher };
    case "inquiry.declined":
      return { label: "Inquiry declined", sub: [str(d.by), str(d.reason)].filter(Boolean).join(" — ") || null, isVoucher };
    case "inquiry.operator_action":
      return { label: `Operator action: ${str(d.action) ?? "unknown"}`, sub: str(d.by), isVoucher };
    case "session.opened":
      return { label: "Session opened", sub: null, isVoucher };
    case "session.submitted":
      return { label: "Session submitted", sub: null, isVoucher };
    case "oauth.connected":
      return { label: `${str(d.provider) ?? "OAuth"} connected`, sub: str(d.profile_name), isVoucher };
    case "oauth.failed":
      return {
        label: `${str(d.provider) ?? "OAuth"} connection failed`,
        sub: str(d.reason) ?? str(d.status),
        isVoucher,
      };
    case "email.verified":
      return { label: "Email verified", sub: str(d.email), isVoucher };
    case "phone.verified":
      return { label: "Phone verified", sub: null, isVoucher };
    case "voucher.invited":
      return {
        label: "Voucher invited",
        sub: [str(d.voucher_email), str(d.relationship)].filter(Boolean).join(" · ") || null,
        isVoucher: true,
      };
    case "voucher.outcome":
      return { label: `Vouch outcome: ${str(d.outcome) ?? "unknown"}`, sub: str(d.voucher_email), isVoucher: true };
    case "email.sent": {
      const to = str(d.to);
      const err = str(d.error);
      return {
        label: err ? "Email failed" : "Email sent",
        sub: [str(d.template), to, err].filter(Boolean).join(" · ") || null,
        isVoucher,
      };
    }
    case "scoring.run": {
      const score = str(d.score);
      const zone = str(d.zone);
      return {
        label: "Scoring run",
        sub: score || zone ? `score ${score ?? "—"}${zone ? ` (${zone})` : ""}` : null,
        isVoucher,
      };
    }
    case "idv.initiated":
      return { label: "IDV started", sub: str(d.marker_status), isVoucher };
    case "idv.captured": {
      const source = str(d.source);
      return {
        label: `IDV captured via ${source ?? "unknown"}`,
        sub: str(d.plaid_status),
        isVoucher,
      };
    }
    default:
      // Unknown event types render raw — this surface must never hide data.
      return { label: e.event, sub: null, isVoucher };
  }
}

/** Tailwind classes for the row's left border + label, by severity. */
export function severityClasses(severity: TimelineEvent["severity"]): { border: string; text: string } {
  if (severity === "error") return { border: "border-red-400", text: "text-red-700" };
  if (severity === "warn") return { border: "border-amber-400", text: "text-amber-700" };
  return { border: "border-slate-200", text: "text-slate-800" };
}
